import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { DataGrid } from '@heroui-pro/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type {
    ImportRow,
    ImportRowEditHandler,
    ImportRowExclusionHandlers,
} from '../../importRows';
import { buildImportTableColumns } from './importTableColumns';
import {
    IMPORT_TABLE_CONTENT_CLASS,
    IMPORT_TABLE_HEADING_HEIGHT_PX,
    IMPORT_TABLE_ROW_HEIGHT_PX,
} from './importTableMeta';

type ImportGridProps = ImportRowExclusionHandlers &
    ImportRowEditHandler & {
        rows: ImportRow[];
        totalRows: number;
        emptyMessage: string;
        /** A row's press; stable for the table's lifetime. */
        onRowAction: (key: React.Key) => void;
    };

const rowIdOf = (row: ImportRow): number => row.rowNumber;

/**
 * The grid of `ImportTable`, apart so it renders only when its rows, its
 * words or its handlers change. Any render of HeroUI Pro's `DataGrid` walks
 * every row it holds (about a second for 2,000 rows under the development
 * build), so nothing that changes while the file is checked or imported
 * (the import's running state, a pending re-check) reaches it as a prop.
 */
export const ImportGrid = React.memo<ImportGridProps>(function ImportGrid({
    rows,
    totalRows,
    emptyMessage,
    onEditRow,
    onExcludeRow,
    onIncludeRow,
    onRowAction,
}) {
    const { t } = useTranslation('pages');
    const columns = React.useMemo(
        () =>
            buildImportTableColumns(t, {
                onEditRow,
                onExcludeRow,
                onIncludeRow,
            }),
        [onEditRow, onExcludeRow, onIncludeRow, t],
    );

    return (
        <DataGrid<ImportRow>
            virtualized
            aria-label={t('catalogImport.products.table.ariaLabel')}
            columns={columns}
            contentClassName={IMPORT_TABLE_CONTENT_CLASS}
            data={rows}
            footer={
                <Typography
                    color="muted"
                    type="body-sm"
                    {...getNodeTestIdProp('catalog-import-products-footer')}
                >
                    {t('catalogImport.products.table.footer', {
                        count: totalRows,
                        shown: countParam(rows.length),
                        total: countParam(totalRows),
                    })}
                </Typography>
            }
            getRowId={rowIdOf}
            headingHeight={IMPORT_TABLE_HEADING_HEIGHT_PX}
            onRowAction={onRowAction}
            renderEmptyState={() => (
                <span {...getNodeTestIdProp('catalog-import-products-empty')}>
                    {emptyMessage}
                </span>
            )}
            rowHeight={IMPORT_TABLE_ROW_HEIGHT_PX}
            variant="primary"
        />
    );
});
